import { useEffect } from 'react';

/** The ring sticks out this many px on each side of the button (the old `box-shadow` spread). */
export const PULSE_RING_SPREAD_PX = 10;

/** `--pulse-from-x/y` for a button of this border box. A box with no size leaves the CSS fallback alone. */
export function applyPulseRingScale(element: HTMLElement, width: number, height: number): void {
  if (!width || !height) return;
  element.style.setProperty('--pulse-from-x', (width / (width + 2 * PULSE_RING_SPREAD_PX)).toFixed(4));
  element.style.setProperty('--pulse-from-y', (height / (height + 2 * PULSE_RING_SPREAD_PX)).toFixed(4));
}

/**
 * Feeds the trial CTA pulse (global.css, `trialCtaPulseRing`) the scale its ring starts from.
 *
 * The ring is a pseudo-element as big as the button plus the spread on each side, scaled from the
 * button's own size up to full size — `transform` only, so it stays on the compositor. The starting scale
 * is `width / (width + 2 × spread)` and `height / (height + 2 × spread)`, which depends on the label
 * length of each locale, so it is measured into `--pulse-from-x/y` on every `[data-pulse-ring]` element.
 * The CSS falls back to the PT label's values until then.
 *
 * Sizes come only from the ResizeObserver, which reports each element once on `observe` and again on every
 * resize, after layout. Reading `offsetWidth` in the effect would force a synchronous layout on every page
 * right after hydration — this hook runs in the header.
 */
export function usePulseRingScale(remeasureKey?: unknown): void {
  useEffect(() => {
    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver((entries) =>
      entries.forEach((entry) => {
        const box = entry.borderBoxSize?.[0];
        applyPulseRingScale(entry.target as HTMLElement, box ? box.inlineSize : entry.contentRect.width, box ? box.blockSize : entry.contentRect.height);
      })
    );
    document.querySelectorAll<HTMLElement>('[data-pulse-ring]').forEach((element) => observer.observe(element));
    return () => observer.disconnect();
  }, [remeasureKey]);
}
